import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';
import { createUser } from '../../../../services/UsersServices';
import { getRoles } from '../../../../services/RoleService';
import { Form, Button, Alert, Col, Row } from 'react-bootstrap';
import moment from 'moment';
import DatePicker from 'react-datepicker';
const AddUser = () => {
  const ssoOptions = [
    { value: 'okta', label: 'Okta' },
    { value: 'azure', label: 'Azure' },
  ];
  const navigate = useNavigate();
  const [error, setError] = useState('');

  const [roleOptions, setRoleOptions] = useState([]);

  useEffect(() => {
    const getRoleList = async () => {
      await getRoles()
        .then((response) => {
          const list = response.map((data) => {
            return { value: data.slug, label: data.label };
          });
          setRoleOptions(list);
        })
        .catch(() => {
          console.log('error while get roles list', error);
          setRoleOptions([]);
        })
        .finally(() => {});
    };
    getRoleList();
  }, []);

  const formik = useFormik({
    initialValues: {
      email: '',
      firstName: '',
      lastName: '',
      employeeNumber: '0',
      password: '',
      sub: '0',
      locale: 'en_US',
      zoneinfo: 'Not set',
      role: { value: 'user', label: 'User' }, // Default to 'User'
      manager: 'Not set',
      displayName: 'Not set',
      managerEmail: 'Not set',
      ssoLoginWith: 'okta',
      dateOfJoining: moment().toDate(),
    },
    validationSchema: Yup.object({
      email: Yup.string().email('Invalid email address').required("Email can't be blank"),
      password: Yup.string()
        .min(8, 'Password must be at least 8 characters')
        .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
        .matches(/[0-9]/, 'Password must contain at least one number')
        .required("Password can't be blank"),
      firstName: Yup.string()
        .matches(/^[a-zA-Z-'`]+$/, 'Must be only letters')
        .required("First name can't be blank"),
      lastName: Yup.string()
        .matches(/^[a-zA-Z-'`]+$/, 'Must be only letters')
        .required("Last name can't be blank"),
    }),
    onSubmit: async (values) => {
      let payload = { ...values, role: values.role.value };
      try {
        await createUser(payload);
        navigate('/settings/users');
      } catch (error) {
        console.error('Error creating user', error);
      }
    },
  });

  return (
    <Form onSubmit={formik.handleSubmit}>
      <h2>Create User</h2>
      {error && <Alert variant="danger">{error}</Alert>}

      <Row>
        <Col>
          <Form.Group controlId="firstName">
            <Form.Label>First Name</Form.Label>
            <Form.Control
              type="text"
              {...formik.getFieldProps('firstName')}
              isInvalid={formik.touched.firstName && !!formik.errors.firstName}
            />
            <Form.Control.Feedback type="invalid">{formik.errors.firstName}</Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col>
          <Form.Group controlId="lastName">
            <Form.Label>Last Name</Form.Label>
            <Form.Control
              type="text"
              {...formik.getFieldProps('lastName')}
              isInvalid={formik.touched.lastName && !!formik.errors.lastName}
            />
            <Form.Control.Feedback type="invalid">{formik.errors.lastName}</Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col>
          <Form.Group controlId="employeeNumber">
            <Form.Label>Employee Number</Form.Label>
            <Form.Control type="text" {...formik.getFieldProps('employeeNumber')} />
          </Form.Group>
        </Col>
        <Col>
          <Form.Group controlId="sub">
            <Form.Label>Sub</Form.Label>
            <Form.Control type="text" {...formik.getFieldProps('sub')} />
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col>
          <Form.Group controlId="locale">
            <Form.Label>Locale</Form.Label>
            <Form.Control type="text" {...formik.getFieldProps('locale')} />
          </Form.Group>
        </Col>
        <Col>
          <Form.Group controlId="zoneinfo">
            <Form.Label>Zone Info</Form.Label>
            <Form.Control type="text" {...formik.getFieldProps('zoneinfo')} />
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col>
          <Form.Group controlId="role">
            <Form.Label>Employee Type</Form.Label>
            <Select
              options={roleOptions}
              onChange={(option) => formik.setFieldValue('role', option)}
            />
          </Form.Group>
        </Col>
        <Col>
          <Form.Group controlId="manager">
            <Form.Label>Manager</Form.Label>
            <Form.Control type="text" {...formik.getFieldProps('manager')} />
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col>
          <Form.Group controlId="displayName">
            <Form.Label>Display Name</Form.Label>
            <Form.Control type="text" {...formik.getFieldProps('displayName')} />
          </Form.Group>
        </Col>
        <Col>
          <Form.Group controlId="managerEmail" className="mb-3">
            <Form.Label>Manager Email</Form.Label>
            <Form.Control type="text" {...formik.getFieldProps('managerEmail')} />
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col>
          <Form.Group controlId="email">
            <Form.Label>Email</Form.Label>
            <Form.Control
              type="email"
              {...formik.getFieldProps('email')}
              isInvalid={formik.touched.email && !!formik.errors.email}
            />
            <Form.Control.Feedback type="invalid">{formik.errors.email}</Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col>
          <Form.Group controlId="password">
            <Form.Label>Password</Form.Label>
            <Form.Control
              type="password"
              {...formik.getFieldProps('password')}
              isInvalid={formik.touched.password && !!formik.errors.password}
            />
            <Form.Control.Feedback type="invalid">{formik.errors.password}</Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col>
          <Form.Group controlId="dateOfJoining">
            <Form.Label>Hire Date</Form.Label>
            <DatePicker
              selected={
                formik.values.dateOfJoining
                  ? moment(formik.values.dateOfJoining, 'MM/DD/YYYY').toDate()
                  : null
              }
              onChange={(date) =>
                formik.setFieldValue(
                  'dateOfJoining',
                  date ? moment(date).format('MM/DD/YYYY') : null
                )
              }
              className="form-control"
              dateFormat="MM/dd/yyyy"
            />
            <Form.Control.Feedback type="invalid">
              {formik.errors.dateOfJoining}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col>
          <Form.Group controlId="ssoLoginWith">
            <Form.Label>SSO Login with</Form.Label>
            <Select
              name="ssoLoginWith"
              options={ssoOptions}
              value={ssoOptions.find((option) => option.value === formik.values.ssoLoginWith)}
              onChange={(selectedOption) => {
                formik.setFieldValue('ssoLoginWith', selectedOption ? selectedOption.value : '');
              }}
              onBlur={() => formik.setFieldTouched('ssoLoginWith', true)}
              className={
                formik.touched.ssoLoginWith && formik.errors.ssoLoginWith ? 'is-invalid' : ''
              }
            />
            {formik.touched.ssoLoginWith && formik.errors.ssoLoginWith && (
              <div className="invalid-feedback d-block">{formik.errors.ssoLoginWith}</div>
            )}
          </Form.Group>
        </Col>
      </Row>

      <Button variant="primary" type="submit">
        Create User
      </Button>
    </Form>
  );
};

export default AddUser;
